/**
 * First-run onboarding — pure state logic (the component is Onboarding.tsx).
 *
 *   'name'    «Как тебя зовут?»            nickname (a pseudonym, never the full name)
 *   'address' «Ты мальчик или девочка?»    → StudentProfile.address, needed only for Russian verb endings
 *   'age'     «Сколько тебе лет?»          path 2.0 only (docs/PATH-V2.md §5.3): the band 5–6 / 7–8 / 9+ — only the band
 *                                          is stored (PathStoreDoc.ageBand)
 *   'moves'   «Знаешь, как ходят фигуры?»  path 2.0 only: «нет» → stage 0, «да» → the placement test after the welcome
 *   'welcome' Гамбитик waves and greets    → the app opens the home screen (or the placement)
 *
 * With the path the name is saved on the server at the address step, but the two answers only at «Поехали!»: a reload
 * in between (a tab closed, a phone that slept — review round 2) used to land on the home with no age band and no
 * placement, never asked again. So the reached step and the answers are kept in this browser (`SignupDraft`,
 * localStorage `gambit.signup`, removed at «Поехали!» and with the child's keys on sign-out) and sign-up resumes there.
 */
import type { AgeBand, StudentProfile } from '@gambit/shared';

/** What the server puts into a brand-new profile (apps/server defaultProfile). */
export const DEFAULT_NICKNAME = 'Шахматист';
/** Mirrors the server's PUT /student validation. */
export const NICKNAME_MAX_LENGTH = 24;

export type Address = StudentProfile['address'];
export type OnboardingStep = 'name' | 'address' | 'age' | 'moves' | 'welcome';
export type NicknameProblem = 'empty' | 'tooLong' | 'badChars';

export interface OnboardingState {
  step: OnboardingStep;
  /** raw text of the input */
  nicknameInput: string;
  /** validated nickname, set when the name step was passed */
  nickname: string | null;
  address: Address | null;
  problem: NicknameProblem | null;
  /** present only when «Путь пешки» 2.0 is on: the two questions of §5.3 */
  path?: OnboardingPathChoice;
}

export interface OnboardingPathChoice {
  ageBand: AgeBand | null;
  knowsMoves: boolean | null;
}

export type OnboardingAction =
  | { type: 'typeNickname'; value: string }
  | { type: 'submitNickname' }
  | { type: 'chooseAddress'; address: Address }
  | { type: 'chooseAge'; ageBand: AgeBand }
  | { type: 'chooseMoves'; knowsMoves: boolean }
  /** the server refused the nickname after all — ask again, gently */
  | { type: 'nicknameRejected' }
  | { type: 'back' };

export type NicknameCheck = { ok: true; value: string } | { ok: false; problem: NicknameProblem };

/** The path's sign-up in progress (after the address step, before «Поехали!»): what the child answered so far. */
export interface SignupDraft {
  v: 1;
  nickname: string;
  address: Address;
  ageBand: AgeBand | null;
  knowsMoves: boolean | null;
}

export const SIGNUP_DRAFT_KEY = 'gambit.signup';

export type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** This browser's localStorage (null in private mode / tests without a window). */
export function signupStorage(): DraftStorage | null {
  try {
    return typeof window === 'undefined' || typeof window.localStorage === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

const AGE_BANDS: readonly AgeBand[] = ['5-6', '7-8', '9+'];

export function readSignupDraft(storage: DraftStorage | null): SignupDraft | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SIGNUP_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<SignupDraft> | null;
    if (!d || d.v !== 1 || typeof d.nickname !== 'string' || d.nickname.trim() === '' || (d.address !== 'm' && d.address !== 'f')) return null;
    const ageBand = AGE_BANDS.includes(d.ageBand as AgeBand) ? (d.ageBand as AgeBand) : null;
    const knowsMoves = typeof d.knowsMoves === 'boolean' && ageBand !== null ? d.knowsMoves : null;
    return { v: 1, nickname: d.nickname, address: d.address, ageBand, knowsMoves };
  } catch {
    return null;
  }
}

export function writeSignupDraft(storage: DraftStorage | null, draft: SignupDraft): void {
  try {
    storage?.setItem(SIGNUP_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // full or private mode: a reload then starts the sign-up again (nothing is lost but the answers)
  }
}

export function clearSignupDraft(storage: DraftStorage | null): void {
  try {
    storage?.removeItem(SIGNUP_DRAFT_KEY);
  } catch {
    // nothing to do
  }
}

/** The draft of the state the child is in (the path's steps after the address), null before that. */
export function signupDraftOf(state: OnboardingState): SignupDraft | null {
  if (!state.path || state.nickname === null || state.address === null) return null;
  if (state.step !== 'age' && state.step !== 'moves' && state.step !== 'welcome') return null;
  return { v: 1, nickname: state.nickname, address: state.address, ageBand: state.path.ageBand, knowsMoves: state.path.knowsMoves };
}

/** A draft that belongs to this profile (the name it saved is the profile's). */
function draftOf(profile: Pick<StudentProfile, 'nickname'> | null, draft: SignupDraft | null | undefined): SignupDraft | null {
  return draft && profile && profile.nickname.trim() === draft.nickname.trim() ? draft : null;
}

// The server refuses markup and control characters in a pseudonym: < > \\ | ` and U+0000..U+001F.
const FORBIDDEN_NICKNAME_PUNCTUATION = '<>|`' + String.fromCharCode(92);

function hasForbiddenChar(text: string): boolean {
  for (const char of text) {
    const code = char.charCodeAt(0);
    if (code < 32 || code === 127 || FORBIDDEN_NICKNAME_PUNCTUATION.includes(char)) return true;
  }
  return false;
}

/** Trims and collapses inner whitespace; does not drop or replace any visible character. */
export function normalizeNickname(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

export function checkNickname(raw: string): NicknameCheck {
  // test the raw text too: a tab or a newline must not silently become a space
  if (hasForbiddenChar(raw.trim())) return { ok: false, problem: 'badChars' };
  const value = normalizeNickname(raw);
  if (value === '') return { ok: false, problem: 'empty' };
  // the server counts UTF-16 code units (zod `.max`), so do the same here
  if (value.length > NICKNAME_MAX_LENGTH) return { ok: false, problem: 'tooLong' };
  return { ok: true, value };
}

/** Warm, never blaming. */
export function nicknameProblemRu(problem: NicknameProblem): string {
  switch (problem) {
    case 'empty':
      return 'Напиши, как тебя называть, — хватит и прозвища.';
    case 'tooLong':
      return 'Получилось длинновато. Давай покороче — до 24 букв.';
    case 'badChars':
      return 'Давай без значков вроде < > | — только буквы и цифры.';
  }
}

/**
 * First run = the profile still carries no chosen name. A brand-new server profile is pre-filled
 * with «Шахматист», so that value only counts as "chosen" once onboarding was finished on this
 * computer (`onboardedFlag`, kept in localStorage).
 */
export function needsOnboarding(profile: Pick<StudentProfile, 'nickname'> | null, onboardedFlag: boolean, draft?: SignupDraft | null): boolean {
  if (profile === null) return !onboardedFlag;
  // the path's sign-up was left between the address and «Поехали!»: it goes on (review round 2)
  if (!onboardedFlag && draftOf(profile, draft) !== null) return true;
  const nickname = profile.nickname.trim();
  if (nickname === '') return true;
  return nickname === DEFAULT_NICKNAME && !onboardedFlag;
}

export function initialOnboardingState(profile: Pick<StudentProfile, 'nickname' | 'address'> | null, opts: { path?: boolean; draft?: SignupDraft | null } = {}): OnboardingState {
  const known = profile?.nickname.trim() ?? '';
  const state: OnboardingState = {
    step: 'name',
    // never pre-fill the placeholder pseudonym — the child should see an empty, inviting field
    nicknameInput: known === DEFAULT_NICKNAME ? '' : known,
    nickname: null,
    address: null,
    problem: null,
  };
  if (opts.path === true) {
    state.path = { ageBand: null, knowsMoves: null };
    // a sign-up left after the address (a reload): back on the step it reached, with its answers
    const d = draftOf(profile, opts.draft);
    if (d) {
      const step: OnboardingStep = d.ageBand === null ? 'age' : d.knowsMoves === null ? 'moves' : 'welcome';
      return { ...state, step, nickname: d.nickname, nicknameInput: d.nickname, address: d.address, path: { ageBand: d.ageBand, knowsMoves: d.knowsMoves } };
    }
  }
  return state;
}

/** The steps the child walks (the dots): the path's two questions only when it is on. */
export function onboardingSteps(state: Pick<OnboardingState, 'path'>): readonly OnboardingStep[] {
  return state.path ? ['name', 'address', 'age', 'moves', 'welcome'] : ['name', 'address', 'welcome'];
}

export function onboardingReducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  switch (action.type) {
    case 'typeNickname':
      if (state.step !== 'name') return state;
      // the hint disappears as soon as the child keeps typing
      return { ...state, nicknameInput: action.value, problem: null };

    case 'submitNickname': {
      if (state.step !== 'name') return state;
      const check = checkNickname(state.nicknameInput);
      if (!check.ok) return { ...state, problem: check.problem };
      return { ...state, step: 'address', nickname: check.value, nicknameInput: check.value, problem: null };
    }

    case 'chooseAddress':
      if (state.step !== 'address' || state.nickname === null) return state;
      return { ...state, step: state.path ? 'age' : 'welcome', address: action.address };

    case 'chooseAge':
      if (state.step !== 'age' || !state.path) return state;
      return { ...state, step: 'moves', path: { ...state.path, ageBand: action.ageBand } };

    case 'chooseMoves':
      if (state.step !== 'moves' || !state.path) return state;
      return { ...state, step: 'welcome', path: { ...state.path, knowsMoves: action.knowsMoves } };

    case 'nicknameRejected':
      return { ...state, step: 'name', nickname: null, address: null, problem: 'badChars' };

    case 'back':
      if (state.step === 'address') return { ...state, step: 'name', problem: null };
      if (state.step === 'age') return { ...state, step: 'address' };
      if (state.step === 'moves') return { ...state, step: 'age' };
      if (state.step === 'welcome') return { ...state, step: state.path ? 'moves' : 'address' };
      return state;
  }
}

/** The PUT /student body once both questions are answered; null before that. */
export function onboardingResult(state: OnboardingState): { nickname: string; address: Address } | null {
  if (state.nickname === null || state.address === null) return null;
  return { nickname: state.nickname, address: state.address };
}

/** The path's answers once both are given (§5.3); null before that or without the path. */
export function onboardingPathResult(state: Pick<OnboardingState, 'path'>): { ageBand: AgeBand; knowsMoves: boolean } | null {
  if (!state.path || state.path.ageBand === null || state.path.knowsMoves === null) return null;
  return { ageBand: state.path.ageBand, knowsMoves: state.path.knowsMoves };
}
